// Row 28.5 — onboarding tour. Composes CAT22's existing WizardTemplate (Stepper + Next/Back/
// Finish, focus-management + aria-live already built there) inside a Modal, rather than
// inventing a new step-through mechanism or adopting a spotlight-overlay tour library
// (disproportionate dependency weight for a LOW-severity row) — hand-rolled, small, real.
import { useState } from "react";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Modal, WizardTemplate, type WizardStep } from "@perlab/ui";

export interface OnboardingTourProps {
  open: boolean;
  onClose: () => void;
}

export function OnboardingTour({ open, onClose }: OnboardingTourProps): ReactElement {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);

  const steps: WizardStep[] = [
    { id: "shell", label: t("tour.shell.label"), content: <p>{t("tour.shell.body")}</p> },
    { id: "scope", label: t("tour.scope.label"), content: <p>{t("tour.scope.body")}</p> },
    { id: "help", label: t("tour.help.label"), content: <p>{t("tour.help.body")}</p> },
  ];

  // Reopening always restarts at step 1 — WizardTemplate stays mounted (inside the
  // permanently-mounted HelpMenu) across close/reopen, so activeIndex must be reset on
  // EVERY close path (×, Escape, backdrop, Finish), not just Finish.
  const handleClose = () => {
    setActiveIndex(0);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} title={t("tour.title")}>
      {/* Empty title here (Modal's header already carries the visible + accessible-name
          title) avoids rendering "Take a tour" twice inside one dialog. */}
      <WizardTemplate
        title=""
        steps={steps}
        activeIndex={activeIndex}
        onActiveIndexChange={setActiveIndex}
        onFinish={handleClose}
      />
    </Modal>
  );
}
